import { Alert, Button } from '@components'
import { useBusinessContext } from '@context'
import { invalidateDashboardQueries, IVatPeriod, reportVatPeriodRecapitulative } from '@query'
import { openInteractiveEuSalesReport } from '@pages/ReportsPage/interactive/interactiveReportsSession'
import { isGermanBusinessCountry } from '@pages/InvoicingPage/xrechnungDelivery'
import { formatDate } from '@root/utils'
import React from 'react'
import toast from 'react-hot-toast'
import { Trans, useTranslation } from 'react-i18next'
import { FaCheckCircle } from 'react-icons/fa'
import { useHistory } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'

interface Props {
  vatPeriod: IVatPeriod
  onClose: () => void
}

const FI_GUIDE_URL =
  'https://www.vero.fi/yritykset-ja-yhteisot/verot-ja-maksut/arvonlisaverotus/yhteenvetoilmoitus/'
const DE_GUIDE_URL =
  'https://www.bzst.de/DE/Unternehmen/Umsatzsteuer/ZusammenfassendeMeldung/zusammenfassendemeldung_node.html'

export const EuRecapReportModal: React.FC<Props> = ({ vatPeriod, onClose }) => {
  const queryClient = useQueryClient()
  const history = useHistory()
  const { slug, data: business } = useBusinessContext()
  const [t] = useTranslation()
  const isGerman = isGermanBusinessCountry(business?.country)
  const dueDate = vatPeriod.recapitulative_due_date
    ? formatDate(vatPeriod.recapitulative_due_date)
    : ''

  const reportMutation = useMutation({
    mutationFn: () => reportVatPeriodRecapitulative({ slug, vatPeriodId: vatPeriod.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [slug, 'vat_periods'] })
      queryClient.invalidateQueries({ queryKey: [slug, 'vat_periods', vatPeriod.id] })
      void invalidateDashboardQueries(queryClient, slug)
      toast.success(t('vatPage.euRecap.success'))
      onClose()
    },
    onError: () => {
      toast.error(t('error.oho'))
    }
  })

  const openEuSalesReport = () => {
    openInteractiveEuSalesReport(history, slug)
    onClose()
  }

  return (
    <Content>
      <Alert
        type="info"
        marginBottom={0}
        description={
          isGerman ? (
            <Trans i18nKey="vatPage.euRecap.modal.bodyDe" values={{ dueDate }}>
              <a href={DE_GUIDE_URL} target="_blank" rel="noopener noreferrer" />
              <strong />
            </Trans>
          ) : (
            <Trans i18nKey="vatPage.euRecap.modal.bodyFi" values={{ dueDate }}>
              <a href={FI_GUIDE_URL} target="_blank" rel="noopener noreferrer" />
              <strong />
            </Trans>
          )
        }
      />
      <ReportLink type="button" onClick={openEuSalesReport}>
        {t('vatPage.euRecap.modal.openReport')}
      </ReportLink>
      <ButtonRow>
        <Button type="button" intent="default" onClick={onClose}>
          {t('general.cancel')}
        </Button>
        <Button
          type="button"
          intent="primary"
          icon={<FaCheckCircle />}
          showSpinner={reportMutation.isPending}
          disabled={reportMutation.isPending || vatPeriod.is_recapitulative_reported}
          onClick={() => reportMutation.mutate()}
        >
          {t('vatPage.euRecap.markReported')}
        </Button>
      </ButtonRow>
    </Content>
  )
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;

  a {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
  }
`

const ReportLink = styled.button`
  appearance: none;
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  font: inherit;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};

  &:hover {
    text-decoration: underline;
  }
`

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`
